class CustomTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.activeTab = null;
    this.instanceId = this.generateInstanceId();
    this.defaultTab = parseInt(this.dataset.defaultTab, 10) || null;
    
    // Wait for the component to be fully connected to the DOM
    if (this.isConnected) {
      this.init();
    } else {
      this.addEventListener('connectedCallback', () => this.init());
    }
  }

  generateInstanceId() {
    return 'tabs-' + Math.random().toString(36).substr(2, 9);
  }

  init() {
    this.createNavigation();
    
    // Only proceed if we have tabs
    if (this.tabs.length > 0) {
      this.bindEvents();
      this.showTab(this.defaultTab ? this.tabs[this.defaultTab - 1]?.id : this.tabs[0]?.id || null);
    }
  }

  createNavigation() {
    const tabLinks = this.querySelectorAll('.tabs__tab-link');
    const navContainer = this.querySelector('.tabs__buttons');
    
    if (!navContainer || tabLinks.length === 0) return;

    // Clear existing navigation
    navContainer.innerHTML = '';

    tabLinks.forEach((link, index) => {
      const originalTabId = link.getAttribute('data-tab');
      const uniqueTabId = `${this.instanceId}-${originalTabId}`;
      const tabLabel = link.getAttribute('data-label') || link.querySelector('.tabs__tab-label')?.textContent || `Tab ${index + 1}`;
      const tabUrl = link.getAttribute('data-url');

      // Update the link's data-tab attribute to be unique
      link.setAttribute('data-tab', uniqueTabId);

      // Find the tab content for this link by traversing to its parent tab container
      const tabContent = link.closest('.tabs__tab');

      // Only add tab if content is found
      if (tabContent) {
        // Store tab reference
        this.tabs.push({
          id: uniqueTabId,
          originalId: originalTabId,
          link: link,
          content: tabContent,
          label: tabLabel,
          url: tabUrl
        });

        let navButton;
        if (tabUrl) {
          // If there's a URL, create a link
          navButton = document.createElement('a');
          navButton.setAttribute('href', tabUrl);
          navButton.setAttribute('target', '_self');
        } else {
          // If no URL, create a button for tab switching
          navButton = document.createElement('button');
          navButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.showTab(uniqueTabId);
          });
        }
        navButton.className = 'tabs__nav-button';
        navButton.setAttribute('data-tab', uniqueTabId);
        navButton.setAttribute('role', 'tab');
        navButton.textContent = tabLabel;

        navContainer.appendChild(navButton);
      }
    });
  }

  bindEvents() {
    const navButtons = this.querySelectorAll('.tabs__nav-button');
    
    navButtons.forEach(button => {
      // Solo agregar evento si es un botón, no un enlace
      if (button.tagName.toLowerCase() === 'button') {
        button.addEventListener('click', (e) => {
          const tabId = e.currentTarget.getAttribute('data-tab');
          this.showTab(tabId);
        });
      }
    });
  }

  showTab(tabId) {
    if (!tabId) return;

    // Hide all tabs
    this.tabs.forEach(tab => {
      if (tab.content) {
        tab.content.style.display = 'none';
      }
    });

    // Remove active state from all navigation buttons
    const navButtons = this.querySelectorAll('.tabs__nav-button');
    navButtons.forEach(button => {
      button.setAttribute('aria-selected', 'false');
      button.classList.remove('tabs__nav-button--active');
    });

    // Show selected tab
    const selectedTab = this.tabs.find(tab => tab.id === tabId);
    if (selectedTab && selectedTab.content) {
      selectedTab.content.style.display = 'block';
      this.activeTab = tabId;
    }

    // Update navigation button state (only target visible nav buttons)
    const activeButton = this.querySelector(`.tabs__nav-button[data-tab="${tabId}"]`);
    if (activeButton) {
      activeButton.setAttribute('aria-selected', 'true');
      activeButton.classList.add('tabs__nav-button--active');
    }
  }

  // Ensure initialization when component is connected
  connectedCallback() {
    if (!this.tabs.length) {
      this.init();
    }
  }

  // Clean up when component is removed
  disconnectedCallback() {
    this.tabs = [];
    this.activeTab = null;
  }
}

// Register the custom element only if it hasn't been registered already
if (!customElements.get('custom-tabs')) {
  customElements.define('custom-tabs', CustomTabs);
}
